import { useEffect, useState } from "react";
import { useSearchParams } from "@/lib/router";
import useAppNavigate from "@/hooks/useAppNavigate";
import { captureSentryException } from "@/services/sentry";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { useSigninCheck } from "reactfire";

const BASE64URL_DASH = /-/g;
const BASE64URL_UNDERSCORE = /_/g;

// Firebase custom tokens are JWTs carrying the target `uid` in the payload.
const getCustomTokenUid = (token: string): string | undefined => {
  try {
    const [, payload = ""] = token.split(".");
    const json = atob(
      payload.replace(BASE64URL_DASH, "+").replace(BASE64URL_UNDERSCORE, "/"),
    );
    const claims: unknown = JSON.parse(json);
    if (
      typeof claims === "object" &&
      claims !== null &&
      "uid" in claims &&
      typeof claims.uid === "string"
    ) {
      return claims.uid;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

const useAuthTokenCheck = () => {
  const auth = getAuth();
  const { status, data } = useSigninCheck();
  const [searchParams] = useSearchParams();
  const navigateTo = useAppNavigate();

  const [checkingToken, setCheckingToken] = useState(true);
  const currentUid = data?.signedIn ? data.user?.uid : undefined;

  useEffect(() => {
    if (status === "loading") {
      return;
    }
    const authToken = searchParams.get("auth-token");
    const isTokenForCurrentUser =
      !!currentUid && getCustomTokenUid(authToken ?? "") === currentUid;

    const signInWithToken = async (token: string) => {
      setCheckingToken(true);
      try {
        await signInWithCustomToken(auth, token);
      } finally {
        setCheckingToken(false);
        navigateTo(window.location.pathname, {
          noPreserveKey: ["auth-token"],
        });
      }
    };

    if (authToken && !isTokenForCurrentUser) {
      signInWithToken(authToken).catch(captureSentryException);
    } else {
      setCheckingToken(false);
    }
  }, [auth, currentUid, searchParams, status]);

  return { checkingToken };
};

export default useAuthTokenCheck;
